import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { Save, Upload, MapPin, Image, Palette, Coins, Plus, Minus, Download, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';

const AssetUploader = ({ label, hint, currentUrl, assetType, onUpload }) => {
    const [uploading, setUploading] = useState(false);
    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/settings/upload/${assetType}`, fd, {
                withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(`${label} berhasil diupload`);
            onUpload(res.data.url);
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal upload'); }
        setUploading(false);
    };
    return (
        <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">{label}</Label>
            <div className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors ${
                uploading ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-primary/40 hover:bg-slate-50'}`}>
                {currentUrl ? (
                    <div className="space-y-2">
                        <img src={currentUrl} alt={label} className="max-h-20 mx-auto object-contain rounded" />
                        <label className="cursor-pointer">
                            <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`upload-${assetType}`} />
                            <span className="inline-flex items-center gap-1.5 text-xs text-primary font-medium hover:underline">
                                <Upload className="w-3.5 h-3.5" /> Ganti gambar
                            </span>
                        </label>
                    </div>
                ) : (
                    <label className="cursor-pointer block">
                        <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`upload-${assetType}`} />
                        <div className="flex flex-col items-center gap-2 py-2">
                            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                                {uploading ? <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /> : <Image className="w-5 h-5 text-slate-400" />}
                            </div>
                            <p className="text-sm font-medium text-slate-600">{uploading ? 'Mengupload...' : 'Klik untuk upload'}</p>
                            <p className="text-xs text-slate-400">PNG, JPG hingga 5MB</p>
                        </div>
                    </label>
                )}
            </div>
        </div>
    );
};

export default function SettingsPage() {
    const { t } = useLang();
    const [settings, setSettings] = useState({ rt_number: '', rw_number: '', kelurahan: '', kecamatan: '', kota: '', provinsi: '', theme_color: '#065f46' });
    const [assets, setAssets] = useState({ logo_url: null, stamp_url: null, org_photo_url: null, signature_ketua_url: null, signature_sekretaris_url: null });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rates, setRates] = useState({ warga: 50000, pendatang: 75000, domisili: 30000 });
    const [savingRates, setSavingRates] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [restoreConfirm, setRestoreConfirm] = useState('');
    const [showRestoreDialog, setShowRestoreDialog] = useState(false);
    const [pendingRestoreFile, setPendingRestoreFile] = useState(null);
    const restoreInputRef = useRef();

    useEffect(() => {
        axios.get(`${API}/settings/regional`, { withCredentials: true })
            .then(r => {
                const { logo_url, stamp_url, org_photo_url, signature_ketua_url, signature_sekretaris_url, background_url, ...rest } = r.data;
                setSettings(rest);
                setAssets({ logo_url, stamp_url, org_photo_url, signature_ketua_url, signature_sekretaris_url, background_url });
            })
            .catch(() => toast.error('Gagal memuat pengaturan'))
            .finally(() => setLoading(false));
        axios.get(`${API}/settings/contribution-rates`, { withCredentials: true })
            .then(r => setRates(r.data)).catch(() => {});
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/settings/regional`, settings, { withCredentials: true });
            toast.success('Pengaturan wilayah disimpan');
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
        setSaving(false);
    };

    const handleDownloadBackup = async () => {
        try {
            const res = await axios.get(`${API}/settings/backup`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `rt_backup_${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            toast.success('Backup berhasil diunduh');
        } catch (e) { toast.error('Gagal membuat backup'); }
    };

    const handleRestoreFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.endsWith('.json')) { toast.error('Hanya file .json yang diizinkan'); return; }
        setPendingRestoreFile(file);
        setRestoreConfirm('');
        setShowRestoreDialog(true);
    };

    const handleRestoreConfirm = async () => {
        if (restoreConfirm !== 'RESTORE') { toast.error('Ketik RESTORE untuk konfirmasi'); return; }
        if (!pendingRestoreFile) return;
        setRestoring(true);
        try {
            const fd = new FormData();
            fd.append('file', pendingRestoreFile);
            const res = await axios.post(`${API}/settings/restore`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(`Restore berhasil! Data dari ${new Date(res.data.backup_date).toLocaleDateString('id-ID')} dipulihkan`);
            setShowRestoreDialog(false);
            setPendingRestoreFile(null);
            setTimeout(() => window.location.reload(), 1500);
        } catch (e) { toast.error(e.response?.data?.detail || 'Restore gagal'); }
        setRestoring(false);
    };

    const handleSaveRates = async () => {
        setSavingRates(true);
        try {
            await axios.put(`${API}/settings/contribution-rates`, rates, { withCredentials: true });
            toast.success('Tarif iuran disimpan');
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan tarif'); }
        setSavingRates(false);
    };

    if (loading) return (
        <div className="space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-64 w-full rounded-xl" />
        </div>
    );

    return (
        <div className="space-y-5" data-testid="settings-page">
            <div>
                <h1 className="page-header text-2xl">{t.settingsTitle}</h1>
                <p className="text-sm text-slate-500 mt-1">Konfigurasi identitas dan aset digital RT</p>
            </div>

            <Tabs defaultValue="regional" className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 pt-4 border-b border-slate-100">
                    <TabsList className="bg-slate-100">
                        <TabsTrigger value="regional" data-testid="tab-regional">
                            <MapPin className="w-4 h-4 mr-1.5" /> {t.regionalTab}
                        </TabsTrigger>
                        <TabsTrigger value="assets" data-testid="tab-assets">
                            <Image className="w-4 h-4 mr-1.5" /> {t.assetsTab}
                        </TabsTrigger>
                        <TabsTrigger value="iuran" data-testid="tab-iuran">
                            <Coins className="w-4 h-4 mr-1.5" /> Tarif Iuran
                        </TabsTrigger>
                        <TabsTrigger value="backup" data-testid="tab-backup">
                            <Download className="w-4 h-4 mr-1.5" /> Backup
                        </TabsTrigger>
                    </TabsList>
                </div>

                {/* Regional Settings */}
                <TabsContent value="regional" className="p-6">
                    <div className="max-w-lg space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label className="font-semibold">{t.rtNumber}</Label>
                                <Input value={settings.rt_number || ''} onChange={e => setSettings(s => ({ ...s, rt_number: e.target.value }))}
                                    placeholder="001" data-testid="setting-rt-number" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="font-semibold">{t.rwNumber}</Label>
                                <Input value={settings.rw_number || ''} onChange={e => setSettings(s => ({ ...s, rw_number: e.target.value }))}
                                    placeholder="001" data-testid="setting-rw-number" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="font-semibold">{t.kelurahan}</Label>
                            <Input value={settings.kelurahan || ''} onChange={e => setSettings(s => ({ ...s, kelurahan: e.target.value }))}
                                placeholder="Nama Kelurahan" data-testid="setting-kelurahan" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="font-semibold">{t.kecamatan}</Label>
                            <Input value={settings.kecamatan || ''} onChange={e => setSettings(s => ({ ...s, kecamatan: e.target.value }))}
                                placeholder="Nama Kecamatan" data-testid="setting-kecamatan" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="font-semibold">{t.kotaKab}</Label>
                            <Input value={settings.kota || ''} onChange={e => setSettings(s => ({ ...s, kota: e.target.value }))}
                                placeholder="Kota / Kabupaten" data-testid="setting-kota" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="font-semibold">Provinsi</Label>
                            <Input value={settings.provinsi || ''} onChange={e => setSettings(s => ({ ...s, provinsi: e.target.value }))}
                                placeholder="Nama Provinsi" data-testid="setting-provinsi" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="font-semibold">Warna Tema</Label>
                            <div className="flex items-center gap-3">
                                <input type="color" value={settings.theme_color || '#065f46'}
                                    onChange={e => setSettings(s => ({ ...s, theme_color: e.target.value }))}
                                    className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-1" />
                                <Input value={settings.theme_color || '#065f46'}
                                    onChange={e => setSettings(s => ({ ...s, theme_color: e.target.value }))}
                                    className="w-32 font-mono text-sm" />
                            </div>
                        </div>
                        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 gap-2" data-testid="save-settings-btn">
                            <Save className="w-4 h-4" /> {saving ? 'Menyimpan...' : t.saveSettings}
                        </Button>
                    </div>
                </TabsContent>

                {/* Digital Assets */}
                <TabsContent value="assets" className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <AssetUploader label={t.uploadLogo} hint="Logo resmi RT" currentUrl={assets.logo_url}
                            assetType="logo" onUpload={url => setAssets(a => ({ ...a, logo_url: url }))} />
                        <AssetUploader label={t.uploadStamp} hint="Stempel resmi RT" currentUrl={assets.stamp_url}
                            assetType="stamp" onUpload={url => setAssets(a => ({ ...a, stamp_url: url }))} />
                        <AssetUploader label="Foto Susunan Pengurus" hint="Foto struktur organisasi RT" currentUrl={assets.org_photo_url}
                            assetType="org_photo" onUpload={url => setAssets(a => ({ ...a, org_photo_url: url }))} />
                        <AssetUploader label={t.uploadSigKetua} hint="Tanda tangan Ketua RT" currentUrl={assets.signature_ketua_url}
                            assetType="signature_ketua" onUpload={url => setAssets(a => ({ ...a, signature_ketua_url: url }))} />
                        <AssetUploader label={t.uploadSigSek} hint="Tanda tangan Sekretaris" currentUrl={assets.signature_sekretaris_url}
                            assetType="signature_sekretaris" onUpload={url => setAssets(a => ({ ...a, signature_sekretaris_url: url }))} />
                    </div>
                    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-sm font-semibold text-amber-800">Catatan Penggunaan Aset Digital</p>
                        <p className="text-xs text-amber-700 mt-1">Logo, stempel, dan tanda tangan digital akan otomatis muncul di header surat resmi dan laporan keuangan RT. Foto susunan pengurus akan tampil di halaman publik.</p>
                    </div>
                </TabsContent>

                {/* Contribution Rates */}
                <TabsContent value="iuran" className="p-6">
                    <div className="max-w-lg space-y-5">
                        <p className="text-sm text-slate-600">Atur tarif iuran bulanan berdasarkan jenis warga. Tambahan untuk Berdagang dan pengurangan untuk Kurang Mampu diatur langsung di data warga masing-masing.</p>
                        {[
                            { key: 'warga', label: 'Warga (Penduduk tetap)', desc: 'Tarif dasar warga yang tinggal menetap' },
                            { key: 'pendatang', label: 'Pendatang / Kontrak', desc: 'Tarif dasar warga dari luar yang menyewa/kontrak' },
                            { key: 'domisili', label: 'Domisili (Alamat saja)', desc: 'Tarif dasar menggunakan alamat untuk administrasi' },
                        ].map(item => (
                            <div key={item.key} className="space-y-1.5">
                                <div>
                                    <Label className="font-semibold">{item.label}</Label>
                                    <p className="text-xs text-slate-500">{item.desc}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-slate-500 font-medium">Rp</span>
                                    <Input type="number" min="0" value={rates[item.key] || 0}
                                        onChange={e => setRates(r => ({ ...r, [item.key]: Number(e.target.value) }))}
                                        className="max-w-40" data-testid={`rate-${item.key}`} />
                                    <span className="text-xs text-slate-400">/ bulan</span>
                                </div>
                            </div>
                        ))}
                        <Button onClick={handleSaveRates} disabled={savingRates} className="bg-primary hover:bg-primary/90 gap-2" data-testid="save-rates-btn">
                            <Save className="w-4 h-4" /> {savingRates ? 'Menyimpan...' : 'Simpan Tarif Iuran'}
                        </Button>
                    </div>
                </TabsContent>
                {/* Backup & Restore */}
                <TabsContent value="backup" className="p-6">
                    <div className="max-w-lg space-y-6">
                        <p className="text-sm text-slate-600">Buat salinan data sistem atau pulihkan dari file backup sebelumnya.</p>

                        {/* Backup */}
                        <div className="p-5 border border-slate-200 rounded-xl space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Download className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="font-bold text-slate-800">Download Backup</p>
                                    <p className="text-sm text-slate-500 mt-0.5">Unduh semua data sistem (warga, keuangan, pengguna, dll) sebagai file JSON.</p>
                                </div>
                            </div>
                            <Button onClick={handleDownloadBackup} className="w-full gap-2 bg-blue-600 hover:bg-blue-700" data-testid="download-backup-btn">
                                <Download className="w-4 h-4" /> Download Backup Sekarang
                            </Button>
                        </div>

                        {/* Restore */}
                        <div className="p-5 border border-red-200 bg-red-50 rounded-xl space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <RotateCcw className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <p className="font-bold text-red-800">Restore dari Backup</p>
                                    <p className="text-sm text-red-600 mt-0.5">⚠️ <strong>Peringatan:</strong> Operasi ini akan menghapus semua data yang ada dan menggantinya dengan data dari file backup. Pastikan Anda sudah memiliki backup terbaru.</p>
                                </div>
                            </div>
                            <input type="file" accept=".json" ref={restoreInputRef} onChange={handleRestoreFileSelect} className="hidden" data-testid="restore-file-input" />
                            <Button onClick={() => restoreInputRef.current?.click()} variant="outline" className="w-full gap-2 border-red-300 text-red-700 hover:bg-red-100" data-testid="restore-btn">
                                <RotateCcw className="w-4 h-4" /> Pilih File Backup untuk Restore
                            </Button>
                        </div>

                        {/* Restore Confirm Dialog */}
                        {showRestoreDialog && (
                            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                                <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                                            <RotateCcw className="w-6 h-6 text-red-600" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-red-800 text-lg">Konfirmasi Restore</p>
                                            <p className="text-sm text-slate-500">File: {pendingRestoreFile?.name}</p>
                                        </div>
                                    </div>
                                    <p className="text-sm text-slate-700 mb-4">Semua data saat ini akan <strong>dihapus dan digantikan</strong> dengan data dari backup. Tindakan ini tidak dapat dibatalkan.</p>
                                    <p className="text-sm font-semibold text-slate-700 mb-2">Ketik <span className="font-mono bg-red-100 text-red-700 px-1.5 py-0.5 rounded">RESTORE</span> untuk konfirmasi:</p>
                                    <input type="text" value={restoreConfirm}
                                        onChange={e => setRestoreConfirm(e.target.value)}
                                        placeholder="Ketik RESTORE" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4 font-mono focus:ring-2 focus:ring-red-400 outline-none" data-testid="restore-confirm-input" />
                                    <div className="flex gap-3">
                                        <Button variant="outline" onClick={() => { setShowRestoreDialog(false); setPendingRestoreFile(null); }} className="flex-1">Batal</Button>
                                        <Button onClick={handleRestoreConfirm} disabled={restoreConfirm !== 'RESTORE' || restoring}
                                            className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-restore-btn">
                                            {restoring ? 'Memulihkan...' : 'Restore Sekarang'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
